// /web/src/lib/api.js
import { useAuth0 } from "@auth0/auth0-react";
import { useMemo } from "react";
import { trackAmplitudeEvent } from "../lib/amplitude";
import { appendApiPath, getApiBaseUrl } from "./config.js";

// ---------- Shared ----------
const base = getApiBaseUrl(); // e.g. https://api.pebbledash.co
const audience = import.meta.env.VITE_AUTH0_AUDIENCE; // Auth0 API Identifier

function redirectTo(url) {
  try { window.location.href = url; } catch { /* noop */ }
}

// ---------- Main API fetcher ----------
export function useApi() {
  const { getAccessTokenSilently, loginWithRedirect, isAuthenticated } = useAuth0();

  return useMemo(() => {
    return async (path, opts = {}) => {
      const url = appendApiPath(base, path);
      const method = (opts.method || "GET").toUpperCase();

      // Detect calculator calls for analytics
      const isCalc = typeof path === "string" && /\/calc\//i.test(path);
      const typeMatch = typeof path === "string" ? path.match(/\/calc\/([^/?#]+)/i) : null;
      const calc_type = typeMatch ? typeMatch[1] : "unknown";

      const getProjectId = () => {
        const b = opts?.body && typeof opts.body === "object" ? opts.body : null;
        return (
          opts?.project_id ??
          (b && (b.project_id || b.projectId || b.project)) ??
          null
        );
      };

      // Acquire token (with one retry)
      let token;
      try {
        token = await getAccessTokenSilently({
          authorizationParams: { audience },
          timeoutInSeconds: 10,
          detailedResponse: false,
        });
      } catch (err) {
        try {
          token = await getAccessTokenSilently({
            authorizationParams: { audience },
            timeoutInSeconds: 10,
            cacheMode: "off",
            detailedResponse: false,
          });
        } catch (err2) {
          const baseMsg =
            err2?.error || err2?.message || err?.error || err?.message || "Auth token error";
          const hint =
            "Check Auth0: Allowed Web Origins include your app URL(s) and VITE_AUTH0_AUDIENCE matches your API Identifier.";
          if (isAuthenticated) {
            try {
              await loginWithRedirect({
                authorizationParams: { audience, prompt: "login" },
                appState: { returnTo: window.location.pathname + window.location.search },
              });
            } catch {}
          }
          throw new Error(`${baseMsg}. ${hint}`);
        }
      }

      // Emit 'started' + timer for calc runs
      let startedAt = 0;
      if (isCalc) {
        startedAt = performance.now();
        trackAmplitudeEvent("calc_run_started", {
          calc_type,
          method,
          path,
          project_id: getProjectId(),
        });
      }

      // Prepare headers (avoid forcing JSON for GET without body)
      const headers = {
        Authorization: `Bearer ${token}`,
        ...(opts.headers || {}),
      };
      const hasBody = opts.body !== undefined && opts.body !== null;
      const body =
        hasBody && typeof opts.body !== "string"
          ? JSON.stringify(opts.body)
          : hasBody
          ? opts.body
          : undefined;
      if (hasBody && !headers["Content-Type"]) {
        headers["Content-Type"] = "application/json";
      }

      // Call API
      let res;
      try {
        res = await fetch(url, {
          ...opts,
          method,
          headers,
          body,
        });
      } catch (err) {
        // Network failure (browser never delivered a response)
        const baseMsg = err?.message || "Failed to fetch";
        const origin =
          typeof window !== "undefined" && window.location?.origin
            ? window.location.origin
            : null;
        const corsHint =
          baseMsg === "Failed to fetch" && origin
            ? ` This often means the browser blocked a cross-origin request from ${origin}. ` +
              `Confirm that origin is allowed by the API's CORS configuration and that no browser extension or network filter is blocking https://api.pebbledash.co.`
            : "";

        if (isCalc) {
          const duration_ms = startedAt ? Math.round(performance.now() - startedAt) : undefined;
          trackAmplitudeEvent("calc_run_error", {
            calc_type,
            method,
            path,
            project_id: getProjectId(),
            reason: "network_error",
            message: baseMsg,
            duration_ms,
          });
        }

        throw new Error(`Network error calling API (${url}). ${baseMsg}.${corsHint}`.trim());
      }

      // Non-OK handling (401/402 niceties)
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const data = await res.json();
          if (data?.detail) detail = data.detail;
        } catch {}

        // Out of tokens → nudge to pricing
        if (res.status === 402 || detail === "no_tokens") {
          if (isCalc) {
            const duration_ms = startedAt ? Math.round(performance.now() - startedAt) : undefined;
            trackAmplitudeEvent("calc_run_denied_tokens", {
              calc_type,
              method,
              path,
              project_id: getProjectId(),
              duration_ms,
            });
          }
          try { localStorage.setItem("pd_banner", "no_tokens"); } catch {}
          redirectTo("/pricing?reason=no_tokens");
          const e = new Error("no_tokens");
          e.code = 402;
          throw e;
        }

        // 401 → try login again (single hop; caller can retry after redirect)
        if (res.status === 401) {
          try {
            await loginWithRedirect({
              authorizationParams: { audience, prompt: "login" },
              appState: { returnTo: window.location.pathname + window.location.search },
            });
          } catch {}
        }

        // Other errors
        if (isCalc) {
          const duration_ms = startedAt ? Math.round(performance.now() - startedAt) : undefined;
          trackAmplitudeEvent("calc_run_error", {
            calc_type,
            method,
            path,
            project_id: getProjectId(),
            http_status: res.status,
            detail,
            duration_ms,
          });
        }
        const err = new Error(detail);
        err.code = res.status;
        throw err;
      }

      // OK → parse JSON
      const json = await res.json();

      // Broadcast token updates (header listens for this)
      if (typeof window !== "undefined") {
        try {
          const remainingTokens = (() => {
            if (json && typeof json === "object") {
              if (typeof json.tokens_remaining === "number") return json.tokens_remaining;
              if (typeof json.tokens_left === "number") return json.tokens_left;
              if (json.tokens && typeof json.tokens === "object") {
                if (typeof json.tokens.remaining === "number") return json.tokens.remaining;
                if (typeof json.tokens.left === "number") return json.tokens.left;
              }
            }
            return null;
          })();
          if (remainingTokens !== null) {
            window.dispatchEvent(
              new CustomEvent("pd_tokens_update", { detail: { remaining: remainingTokens } })
            );
          }
        } catch {}
      }

      // Save auth0 sub on /me for analytics identify()
      if (path === "/me" && json?.sub) {
        try { window.localStorage.setItem("auth0_sub", json.sub); } catch {}
      }

      // Success emit for calc calls
      if (isCalc) {
        const duration_ms = startedAt ? Math.round(performance.now() - startedAt) : undefined;
        const elapsed_ms =
          (typeof json?.metadata?.elapsed_ms === "number" && json.metadata.elapsed_ms) ||
          (typeof json?.elapsed_ms === "number" && json.elapsed_ms) ||
          undefined;
        const tokens_spent =
          (typeof json?.tokens?.spent === "number" && json.tokens.spent) ||
          (typeof json?.tokens_spent === "number" && json.tokens_spent) ||
          undefined;

        trackAmplitudeEvent("calc_run_success", {
          calc_type,
          method,
          path,
          project_id: getProjectId(),
          duration_ms,
          ...(elapsed_ms ? { elapsed_ms } : {}),
          ...(tokens_spent ? { tokens_spent } : {}),
        });
      }

      return json;
    };
  }, [getAccessTokenSilently, loginWithRedirect, isAuthenticated]);
}

// ---------- Billing helpers ----------
export function useBilling() {
  const { getAccessTokenSilently } = useAuth0();

  async function authedFetch(path, body) {
    const token = await getAccessTokenSilently({
      authorizationParams: { audience },
      timeoutInSeconds: 10,
    });
    const res = await fetch(appendApiPath(base, path), {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body || {}),
    });
    if (!res.ok) {
      let detail = `HTTP ${res.status}`;
      try { const j = await res.json(); if (j?.detail) detail = j.detail; } catch {}
      const err = new Error(detail);
      err.code = res.status;
      throw err;
    }
    return res.json();
  }

  return useMemo(() => {
    return {
      // Subscription plans
      async startPlanCheckout(plan) {
        trackAmplitudeEvent("upgrade_clicked", { plan });
        const { url } = await authedFetch("/billing/checkout", { mode: "plan", plan });
        trackAmplitudeEvent("checkout_started", { kind: "plan", plan });
        redirectTo(url);
      },

      // PAYG bundles (5 / 10 / 20)
      async startAddonCheckout(bundle) {
        trackAmplitudeEvent("addon_checkout_clicked", { bundle });
        const { url } = await authedFetch("/billing/checkout", { mode: "addon", bundle });
        trackAmplitudeEvent("checkout_started", { kind: "addon", bundle });
        redirectTo(url);
      },

      // Customer portal
      async openBillingPortal() {
        const { url } = await authedFetch("/billing/portal");
        trackAmplitudeEvent("billing_portal_opened");
        redirectTo(url);
      },
    };
  }, [getAccessTokenSilently]);
}
